import React, { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Popconfirm, TreeSelect } from 'antd';
import { Button, Modal } from 'ui';
import Spinner from 'components/Spinner';
import ColorPicker from 'components/ColorPicker';
import moment from 'moment';
import { useResource } from 'hooks/Resources/useResource';
import { useT } from 'hooks/Locale/useT';
import { ResourceProps, ResourceType } from 'components/Resources/types';
import { DataNode } from 'antd/lib/tree';

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
};

const DEFAULT_COLOR = '#1890ff';

interface EventData {
    id?: number;
    title?: string;
    description?: string;
    resourceId?: Number;
    color?: string;
    start?: string | Date;
    end?: string | Date;
    day?: any;
}

interface CalendarEventModalProps {
    visible: boolean;
    onCancel: () => void;
    onSubmit: () => void;
    eventData: EventData | null;
    reservationData: {
        id?: Number;
        resourceId?: Number;
        start?: Date;
        end?: Date;
        day: any;
    };
}

const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
    visible,
    onCancel,
    onSubmit,
    eventData,
    reservationData,
}) => {
    const t = useT();
    const [form] = Form.useForm();
    const { getResources, saveCalendarEvent, deleteCalendarEvent, resourceLoading } = useResource();
    const { RangePicker } = DatePicker;
    const [treeData, setTreeData] = useState<DataNode[]>([]);

    const getFormattedTitle = (resourceType?: string, title?: string): string => {
        const typeTranslation = t(`system.resource.type.${resourceType}`);

        return !!resourceType && resourceType !== ResourceType.Other
            ? `${typeTranslation}${!!title ? ' - ' + title : ''}`
            : title ?? '';
    };

    useEffect(() => {
        const buildTreeData = (resources: ResourceProps[]): DataNode[] =>
            resources.map((resource) => ({
                title: getFormattedTitle(resource.resourceType, resource.title),
                value: resource.id,
                key: resource.id ?? '',
                selectable: false,
                children: resource.children?.map((child: ResourceProps) => ({
                    title: child.title,
                    value: child.id,
                    key: child.id ?? '',
                })),
            }));

        const loadResources = async () => {
            setTreeData(buildTreeData(await getResources()));
        };

        loadResources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (reservationData) {
            form.setFieldsValue({
                resourceId: reservationData.resourceId?.toString(),
                dates: [
                    !!reservationData.start
                        ? moment(reservationData.start)
                        : moment(reservationData.day?.dateStr, 'YYYY-MM-DD')
                              .hours(moment().hours())
                              .startOf('hour'),
                    !!reservationData.start
                        ? moment(reservationData.end ?? reservationData.start)
                        : moment(reservationData.day?.dateStr, 'YYYY-MM-DD')
                              .hours(moment().hours())
                              .add(1, 'hours')
                              .startOf('hour'),
                ],
            });
        }
    }, [reservationData]);

    useEffect(() => {
        if (!eventData) {
            return;
        }

        let initialStart: moment.Moment;
        if (eventData.start) {
            initialStart = moment(eventData.start);
        } else if (eventData.day?.dateStr) {
            initialStart = moment(eventData.day.dateStr, 'YYYY-MM-DD')
                .hours(moment().hours())
                .startOf('hour');
        } else {
            initialStart = moment().startOf('hour');
        }

        const initialEnd = eventData.end
            ? moment(eventData.end)
            : initialStart.clone().add(1, 'hour');

        form.setFieldsValue({
            title: eventData.title ?? '',
            description: eventData.description ?? '',
            resourceId: eventData.resourceId?.toString(),
            color: eventData.color ?? DEFAULT_COLOR,
            dates: [initialStart, initialEnd],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData]);

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    const handleSubmit = async (values: any) => {
        const payload = {
            id: eventData?.id,
            title: values.title,
            description: values.description ?? null,
            color: values.color,
            resourceId: values.resourceId ?? null,
            start: values.dates[0].format(),
            end: values.dates[1].format(),
        };

        if (await saveCalendarEvent(payload)) {
            onSubmit();
        }
    };

    const handleDelete = async () => {
        if (!eventData?.id) return;
        const deleted = await deleteCalendarEvent(eventData.id);
        if (deleted) {
            onSubmit();
        }
    };

    const isEdit = !!eventData?.id;

    const footer: React.ReactNode[] = [
        <Button key="cancel" message="general.cancel" onClick={handleCancel} />,
        <Button
            key="submit"
            message={isEdit ? 'general.save' : 'general.create'}
            type="primary"
            loading={resourceLoading}
            onClick={form.submit}
        />,
    ];

    if (isEdit) {
        footer.unshift(
            <Popconfirm
                key="delete"
                title={t('system.resource.calendar.event.delete_confirm')}
                onConfirm={handleDelete}
                okText={t('general.yes')}
                cancelText={t('general.no')}
                overlayInnerStyle={{ padding: '12px 16px' }}
            >
                <Button danger message="general.delete" />
            </Popconfirm>,
        );
    }

    return (
        <Modal
            destroyOnClose
            open={visible}
            confirmLoading={resourceLoading}
            onCancel={handleCancel}
            title={
                isEdit
                    ? t('system.resource.calendar.event.edit')
                    : t('system.resource.calendar.event.create')
            }
            footer={footer}
        >
            <Spinner spinning={resourceLoading}>
                <Form form={form} onFinish={handleSubmit} {...formItemLayout} preserve={false}>
                    <Form.Item
                        name="title"
                        label={t('system.resource.calendar.event.title')}
                        rules={[{ required: true, message: t('validation.field_required') }]}
                    >
                        <Input maxLength={255} />
                    </Form.Item>

                    <Form.Item
                        name="dates"
                        label={t('general.date')}
                        rules={[{ required: true, message: t('validation.field_required') }]}
                    >
                        <RangePicker
                            showTime={{ format: 'HH:mm', showSecond: false, minuteStep: 15 }}
                            format="YYYY-MM-DD HH:mm"
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('system.resource.calendar.event.description')}
                    >
                        <Input.TextArea rows={3} maxLength={5000} />
                    </Form.Item>

                    <ColorPicker
                        form={form}
                        name="color"
                        label={t('system.resource.calendar.event.color')}
                        initialColor={eventData?.color ?? DEFAULT_COLOR}
                    />

                    <Form.Item
                        name="resourceId"
                        label={t('system.resource.calendar.event.resource')}
                    >
                        <TreeSelect
                            showSearch
                            className={'default-select'}
                            style={{ width: '100%', borderRadius: '8px' }}
                            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                            placeholder={t('general.select')}
                            allowClear
                            showCheckedStrategy={TreeSelect.SHOW_PARENT}
                            treeNodeFilterProp="title"
                            treeData={treeData}
                        />
                    </Form.Item>

                </Form>
            </Spinner>
        </Modal>
    );
};

export default CalendarEventModal;
